
interface Artist {
  id: string;
  name: string;
  avatar: string;
  city: string;
  lanes: string[];
  bioShort: string;
  verified: boolean;
  socials: {
    instagram?: string;
    twitter?: string;
    youtube?: string;
    soundcloud?: string;
    tiktok?: string;
  };
}

interface ArtistHeaderProps {
  artist: Artist;
}

export default function ArtistHeader({ artist }: ArtistHeaderProps) {
  const handleSocialClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex items-start gap-6 flex-1">
      {/* Avatar */}
      <div className="relative">
        <img
          src={artist.avatar}
          alt={artist.name}
          className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
        />
        {artist.verified && (
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
            <i className="ri-check-line text-white text-sm"></i>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">{artist.name}</h1>
          {artist.verified && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
              <i className="ri-check-line mr-1"></i>
              Verified
            </span>
          )}
        </div>

        {/* Location & Lanes */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <i className="ri-map-pin-line mr-1"></i>
            {artist.city}
          </span>
          {artist.lanes.map((lane) => (
            <span
              key={lane}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800"
            >
              {lane}
            </span>
          ))}
        </div>

        {/* Bio */}
        <p className="text-gray-600 text-lg leading-relaxed mb-6 max-w-2xl">
          {artist.bioShort}
        </p>

        {/* Socials */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-500">Follow:</span>
          {artist.socials.instagram && (
            <button
              onClick={() => handleSocialClick(artist.socials.instagram!)}
              className="w-10 h-10 bg-gray-100 hover:bg-pink-100 rounded-full flex items-center justify-center transition-colors group"
            >
              <i className="ri-instagram-line text-gray-600 group-hover:text-pink-600"></i>
            </button>
          )}
          {artist.socials.twitter && (
            <button
              onClick={() => handleSocialClick(artist.socials.twitter!)}
              className="w-10 h-10 bg-gray-100 hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors group"
            >
              <i className="ri-twitter-line text-gray-600 group-hover:text-blue-600"></i>
            </button>
          )}
          {artist.socials.youtube && (
            <button
              onClick={() => handleSocialClick(artist.socials.youtube!)}
              className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors group"
            >
              <i className="ri-youtube-line text-gray-600 group-hover:text-red-600"></i>
            </button>
          )}
          {artist.socials.soundcloud && (
            <button
              onClick={() => handleSocialClick(artist.socials.soundcloud!)}
              className="w-10 h-10 bg-gray-100 hover:bg-orange-100 rounded-full flex items-center justify-center transition-colors group"
            >
              <i className="ri-soundcloud-line text-gray-600 group-hover:text-orange-600"></i>
            </button>
          )}
          {artist.socials.tiktok && (
            <button
              onClick={() => handleSocialClick(artist.socials.tiktok!)}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-800 rounded-full flex items-center justify-center transition-colors group"
            >
              <i className="ri-tiktok-line text-gray-600 group-hover:text-gray-800"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
